import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { cropUnitsByCompanyID } from "graphql/queries";
import { updateCropUnit } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import CropUnitCreateForm from "./CropUnitCreatedForm";
import CropUnitUpdateForm from "./CropUnitUpdateForm";

const CropUnits = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "agro:read",
    "agro:read",
    "agro:read",
  ]);
  const [cropUnits, setCropUnits] = useState([]);
  const [cropUnitsLoading, setCropUnitsLoading] = useState(false);
  const [cropUnitDetails, setCropUnitDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    cropUnitId: null,
    cropUnitName: null,
  });
  const [modalCropUnit, setModalCropUnit] = useState({
    visible: false,
    cropUnitId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      cropUnits.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                cropUnitDetails?.id === item.id
                  ? strings.cropUnits.hide
                  : strings.cropUnits.show
              }
              icon={
                cropUnitDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                cropUnitDetails?.id === item.id
                  ? setCropUnitDetails(null)
                  : setCropUnitDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.cropUnits.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.cropUnits.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    cropUnitId: item.id,
                    cropUnitName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cropUnits, cropUnitDetails?.id]
  );

  const onNext = () => {
    fetchCropUnits(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchCropUnits(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchCropUnits(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalCropUnit({ visible: true, action, cropUnitId: id });
  const onSuccessCropUnit = () => {
    setModalCropUnit({ visible: false });
    fetchCropUnits();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByCropUnits = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setCropUnits((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchCropUnits(token, isNext = true, keywords) {
    const nameOperation = cropUnitsByCompanyID;
    const nameOperationStr = "cropUnitsByCompanyID";
    try {
      setCropUnitsLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const cropUnitsData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const cropUnitsItems = cropUnitsData.data[nameOperationStr].items;
      setCropUnits(cropUnitsItems);
      getNamesByCropUnits(cropUnitsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: cropUnitsData.data[nameOperationStr].nextToken,
        };
      });
      setCropUnitsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setCropUnitsLoading(false);
    } finally {
      setCropUnitsLoading(false);
    }
  }
  async function cropUnitDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateCropUnit, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateCropUnit.id;
      setCropUnits(cropUnits.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        cropUnitId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchCropUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "25%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "25%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "25%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.cropUnits.addCropUnit}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {cropUnitsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!cropUnitDetails?.id} style={styles.collapseCard}>
          <CCard key={cropUnitDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{cropUnitDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b> {cropUnitDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(cropUnitDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.cropUnitName,
        })}
        onCancel={() => setModalDetails({ visible: false, cropUnitId: null })}
        onConfirm={() => cropUnitDelete(modalDetails.cropUnitId)}
      />
      {modalCropUnit.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalCropUnit.visible}
          onClose={() => setModalCropUnit({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalCropUnit.action === "create" && strings.modalCropUnit.addTitle}
              {modalCropUnit.action === "update" && strings.modalCropUnit.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalCropUnit.action === "create" && (
                <CropUnitCreateForm onSuccess={onSuccessCropUnit} />
              )}
              {modalCropUnit.action === "update" && (
                <CropUnitUpdateForm
                  cropUnitId={modalCropUnit.cropUnitId}
                  onSuccess={onSuccessCropUnit}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Unidades de Cultivo",
    successDeleted: "Unidad de Cultivo eliminada con éxito",
    cropUnits: {
      addCropUnit: "Agregar Unidad de Cultivo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
      readPhotos: "Ver Fotos",
      docs: "Documentos",
    },
    responses: {
      errorFetch: "Error al consultar unidad de cultivo",
    },
    table: {
      headers: {
        name: "Nombre",
        companyName: "Empresa",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del CropUnite",
      description: "Descripción",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el cropUnite {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalCropUnit: {
      addTitle: "Agregar Unidad de Cultivo",
      updateTitle: "Actualizar Unidad de Cultivo",
      readPhotosTitle: "Fotos del Unidad de Cultivo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default CropUnits;
